import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ClusterDto, WorkspaceSummaryDto } from '@mogenius/client-sdk/core-dto';
import { ClusterService } from '@mogenius/cluster/services/cluster-service/cluster.service';
import { DashboardService } from '@mogenius/organization/services/dashboard-service/dashboard.service';
import { OrganizationPermissionService } from '@mogenius/organization/services/organization-permission/organization-permission.service';
import { OrganizationService } from '@mogenius/organization/services/organization-service/organization.service';
import commandScore from 'command-score';
import { catchError, of, Subject, Subscription } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { ClusterPage, CommandPaletteService, NavigationResult, WorkspacePage } from './command-palette.service';

@Injectable({ providedIn: 'root' })
export class CommandPaletteNavigationSearchService {
  private readonly _commandPalette = inject(CommandPaletteService);
  private readonly _clusterService = inject(ClusterService);
  private readonly _dashboardService = inject(DashboardService);
  private readonly _permissionService = inject(OrganizationPermissionService);
  private readonly _orgService = inject(OrganizationService);
  private readonly _router = inject(Router);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _searchTrigger$ = new Subject<string>();
  private _searchSubscription: Subscription | null = null;
  private _workspaceLoadRequested = false;

  public init(): void {
    this._searchSubscription?.unsubscribe();

    this._searchSubscription = this._searchTrigger$
      .pipe(
        debounceTime(100),
        filter((q) => q.length >= 1),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe((query) => this.performSearch(query));

    const reSearch = () => {
      if (this._commandPalette.query().length > 0) {
        this.performSearch(this._commandPalette.query());
      }
    };

    this._clusterService.selectedCluster$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(reSearch);
    // The workspace list arrives asynchronously; refresh whatever is on screen when it does.
    this._dashboardService.dashboardData$.pipe(takeUntilDestroyed(this._destroyRef)).subscribe(reSearch);
  }

  public search(query: string): void {
    if (query.length < 1) {
      this._commandPalette.setNavigationResults([]);
      return;
    }
    this._searchTrigger$.next(query);
  }

  private performSearch(query: string): void {
    const results: NavigationResult[] = [];
    const lowerQuery = query.toLowerCase();

    // Offering a cluster the user cannot enter is worse than not offering it at all, so
    // this mirrors clusterPermissionResolver exactly: it gates on the organization's
    // canChange, not on the cluster permission. A user can hold cluster canView (they see
    // the cluster list) and still be rejected on the way in.
    if (this._permissionService.organizationPermission$.value?.canChange) {
      const clusters = this._clusterService.searchResponse$.value?.data ?? [];
      results.push(...this.searchClusters(clusters, lowerQuery));
    }

    results.push(...this.searchWorkspaces(this.accessibleWorkspaces(), lowerQuery));

    // Sort by score and limit to 8 results
    results.sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    this._commandPalette.setNavigationResults(results.slice(0, 8));
  }

  /**
   * Workspaces the user may open, across the whole organization and independent of any
   * selected cluster — that dependency is what made the palette useless for anyone
   * without cluster access. The dashboard endpoint already returns this list filtered to
   * the user's grants, so no separate lookup is needed; it is fetched once on demand.
   */
  private accessibleWorkspaces(): WorkspaceSummaryDto[] {
    const workspaces = this._dashboardService.currentData?.workspaces;
    if (workspaces) return workspaces;

    if (!this._workspaceLoadRequested) {
      this._workspaceLoadRequested = true;
      this._dashboardService
        .loadDashboard(true)
        .pipe(catchError(() => of(null)))
        .subscribe();
    }
    return [];
  }

  private searchClusters(clusters: ClusterDto[], query: string): NavigationResult[] {
    const results: NavigationResult[] = [];

    for (const cluster of clusters) {
      const name = cluster.displayName ?? '';
      if (!name) continue;

      const score = commandScore(name, query);
      const nameContains = name.toLowerCase().includes(query);
      const finalScore = score > 0 ? score : nameContains ? 0.1 : 0;

      if (finalScore <= 0) continue;

      results.push({
        name,
        type: 'cluster',
        icon: 'pi pi-sitemap',
        score: finalScore,
        clusterId: cluster.id,
        execute: () => this.navigateToCluster(cluster, 'overview'),
        openPage: (page) => this.navigateToCluster(cluster, page as ClusterPage)
      });
    }

    return results;
  }

  private searchWorkspaces(workspaces: WorkspaceSummaryDto[], query: string): NavigationResult[] {
    const results: NavigationResult[] = [];

    for (const workspace of workspaces) {
      const name = workspace.name ?? '';
      if (!name) continue;

      const score = commandScore(name, query);
      const nameContains = name.toLowerCase().includes(query);
      const finalScore = score > 0 ? score : nameContains ? 0.1 : 0;

      if (finalScore <= 0) continue;

      results.push({
        name,
        type: 'workspace',
        icon: 'pi pi-th-large',
        parentName: workspace.clusterName,
        score: finalScore,
        clusterId: workspace.clusterId,
        execute: () => this.navigateToWorkspace(workspace, 'dashboard'),
        openPage: (page) => this.navigateToWorkspace(workspace, page as WorkspacePage)
      });
    }

    return results;
  }

  private navigateToCluster(cluster: ClusterDto, page: ClusterPage): void {
    const orgId = this._orgService.selectedOrganization$.value?.id;
    if (!orgId) return;

    this._router.navigate(['/', 'studio', 'organization', orgId, 'cluster', cluster.id, page]);
  }

  private navigateToWorkspace(workspace: WorkspaceSummaryDto, page: WorkspacePage): void {
    const orgId = this._orgService.selectedOrganization$.value?.id;
    if (!orgId || !workspace.clusterId) return;

    // 'workspaces', not 'workspace' — the segment that exists. The wrong one matched no
    // route, fell into the cluster wildcard and redirected to the cluster overview, which
    // is what made cmd+k look like it could only navigate into clusters.
    const target = [
      '/',
      'studio',
      'organization',
      orgId,
      'cluster',
      workspace.clusterId,
      'workspaces',
      workspace.name,
      page
    ];

    // The palette can now offer workspaces of a cluster that is not the selected one, or
    // of none at all. workspaceResolver reads the cluster from ClusterService rather than
    // the route, so select it first or the resolver sends the user back to the list.
    if (workspace.clusterId === this._clusterService.selectedCluster$.value?.id) {
      this._router.navigate(target);
      return;
    }

    this._clusterService
      .cluster(workspace.clusterId, true)
      .pipe(catchError(() => of(null)))
      .subscribe(() => this._router.navigate(target));
  }
}
